/* eslint-disable no-param-reassign */
/* eslint-disable object-shorthand */
/* eslint-disable class-methods-use-this */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import * as Services from '@services';
import {
  AbpUserConfigurationService,
  CreateOrUpdateUserInput,
  GetRoles,
  GetUserOutput,
} from '@services';
import { Utils } from '@utils';
import { message } from 'antd';
import { makeAutoObservable, runInAction } from 'mobx';

declare const abp: any;

class UserStore {
  calendarUsers: Map<number, GetUserOutput> = new Map();
  users: Map<number, GetUserOutput> = new Map();

  // Stable identity until the map changes; the calendar's resource columns are built from it
  // (see orderStore.calendarOrderList).
  get calendarUserList(): GetUserOutput[] {
    return Array.from(this.calendarUsers.values());
  }

  /**
   * Every technician of the tenant, backing the technician selects. Separate from `users`, which is
   * the paged, searchable admin list and gets cleared whenever that screen unmounts - a select
   * sitting in a panel that opens and closes would otherwise refetch a handful of names each time.
   */
  technicians: GetUserOutput[] = [];
  techniciansLoaded: boolean = false;
  techniciansRequest: Promise<void> | undefined;

  loading: boolean = false;
  totalCount: number = 0;
  skipCount: number = 0;
  currentPage: number = 1;
  maxResultCount: number = 15;
  calendarView: 'TechnicianView' | 'WorkStationView' =
    abp.setting.get('App.CalendarView') || 'TechnicianView';

  initiaUser!: CreateOrUpdateUserInput;
  user!: CreateOrUpdateUserInput | undefined;

  roles: GetRoles[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  async create(createUserInput: CreateOrUpdateUserInput) {
    this.setLoading(true);
    try {
      const result = await Services.UserService.create(createUserInput);

      runInAction(() => {
        const newUser = result;

        // Add the new customer to the beginning of the map
        const tempArray = Array.from(this.users.entries());
        tempArray.unshift([newUser.id, newUser]);

        // If there are more than 10 users, remove the oldest one
        if (tempArray.length > 10) {
          tempArray.pop();
        }

        // Clear the map and add the updated list back to it
        this.users.clear();
        tempArray.forEach(([id, user]) => {
          this.users.set(id, user);
        });

        this.totalCount += 1;
      });
      this.setLoading(false);
      message.success({
        content: 'Successfully created!',
      });
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async update(updateUserInput: Services.CreateOrUpdateUserInput) {
    this.setLoading(true);
    try {
      const result = await Services.UserService.update(updateUserInput);
      runInAction(() => {
        this.users.set(result.id, result);
        // Refresh the single-user view so MyAccount fields reflect the saved values.
        this.user = result;
      });
      this.setLoading(false);
      message.success({
        content: 'Successfully updated!',
      });
      return true;
    } catch (error) {
      this.setLoading(false);
      console.error(error);
      return false;
    }
  }

  async delete(entityDto: Services.EntityDto) {
    this.setLoading(true);
    try {
      await Services.UserService.delete(entityDto);
      runInAction(() => {
        this.users.delete(entityDto.id);
        this.totalCount -= 1;
        this.currentPage =
          this.users.size === 0 && this.currentPage > 0 ? this.currentPage - 1 : this.currentPage;
        this.skipCount = this.currentPage > 0 ? (this.currentPage - 1) * 10 : 0;
      });
      this.setLoading(false);
      message.success({
        content: 'Successfully deleted!',
      });
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async getRoles() {
    const result = await Services.UserService.getRoles();
    runInAction(() => {
      this.roles = result;
    });
  }

  async get(entityDto: Services.EntityDto) {
    this.setLoading(true);
    try {
      const result = await Services.UserService.get(entityDto);
      runInAction(() => {
        this.user = result;
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async updateLogo(input: Services.ProfileLogoInput) {
    this.setLoading(true);
    try {
      const result = await Services.UserService.updateLogo(input);
      runInAction(() => {
        this.user!.logo = result;
      });
      this.setLoading(false);
      return result;
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async deleteLogo() {
    this.setLoading(true);
    try {
      await Services.UserService.deleteLogo();
      runInAction(() => {
        this.user!.logo = undefined;
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async getAll(pagedFilterAndSortedRequest: Services.PagedUserResultRequestDto) {
    this.setLoading(true);
    try {
      const result = await Services.UserService.getAll(pagedFilterAndSortedRequest);
      runInAction(() => {
        this.users.clear();
        result.items.forEach((item) => {
          this.users.set(item.id, item);
        });
        this.totalCount = result.totalCount;
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async getCalendarAll(request: Services.PagedUserResultRequestDto) {
    this.setLoading(true);
    try {
      const result = await Services.UserService.getAll(request);
      runInAction(() => {
        result.items.forEach((item) => {
          this.calendarUsers.set(item.id, item);
        });
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async changeLanguage(languageName: string) {
    await Services.UserService.changeLanguage({ languageName: languageName });
  }

  async forceTwoFactor(userId: number, enabled: boolean) {
    this.setLoading(true);
    try {
      await Services.UserService.forceSetTwoFactor(userId, enabled);
      runInAction(() => {
        const user = this.users.get(userId);
        if (user) {
          user.isTwoFactorEnabled = enabled;
        }
      });
      message.success({
        content: `2FA ${enabled ? 'enabled' : 'disabled'} successfully.`,
      });
    } catch (error) {
      console.error(error);
    } finally {
      this.setLoading(false);
    }
  }

  async changePassword(changePasswordInput: Services.ChangePasswordInput) {
    try {
      await Services.UserService.changePassword(changePasswordInput);
      message.success({
        content: 'Successfully changed!',
      });
    } catch (error) {
      console.error(error);
    }
  }

  async updateCalendarViewSetting(value: 'TechnicianView' | 'WorkStationView') {
    try {
      await Services.UserService.updateCalendarViewSetting(value).then(() => {
        AbpUserConfigurationService.getAll().then((data) => {
          Utils.extend(true, abp, data.data.result);
        });
      });
    } catch (error) {
      console.error(error);
    }
  }

  handlePageChange = (pagination: any) => {
    runInAction(() => {
      this.maxResultCount = pagination.pageSize;
      this.setSkipCount((pagination.current - 1) * pagination.pageSize);
      this.setCurrentPage(pagination.current);
    });
  };

  setLoading = (value: boolean) => {
    runInAction(() => {
      this.loading = value;
    });
  };

  clearUser = () => {
    runInAction(() => {
      this.user = undefined;
    });
  };

  /**
   * Loads the technician lookup once; pass force after something changed who is a technician.
   * In-flight requests are shared, because several selects can mount on the same render.
   */
  loadTechnicians(force: boolean = false): Promise<void> {
    if (this.techniciansLoaded && !force) return Promise.resolve();
    if (this.techniciansRequest) return this.techniciansRequest;

    this.techniciansRequest = Services.UserService.getAll({
      technicianOnly: true,
      maxResultCount: 1000,
      skipCount: 0,
    })
      .then((result) => {
        runInAction(() => {
          this.technicians = result.items;
          this.techniciansLoaded = true;
        });
      })
      .catch((error) => console.error(error))
      .finally(() => {
        runInAction(() => {
          this.techniciansRequest = undefined;
        });
      });

    return this.techniciansRequest;
  }

  clearUsers = () => {
    runInAction(() => {
      this.users.clear();
    });
  };

  clearCalendarUsers = () => {
    runInAction(() => {
      this.calendarUsers.clear();
    });
  };

  setSkipCount = (value: number) => {
    runInAction(() => {
      this.skipCount = value;
    });
  };

  setCurrentPage = (value: number) => {
    runInAction(() => {
      this.currentPage = value;
    });
  };
}

export default UserStore;
